import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Activity, ArrowLeft, ArrowRight, ArrowUp, Bluetooth, CalendarDays, Camera, Check, ChevronDown, ChevronLeft, ChevronRight, CircleCheck,
  CirclePlus, Clock, Cloud, Droplet, Dumbbell, Eye, FileText, Flame, FolderOpen, Footprints, Gauge, Globe, Heart, HeartPulse,
  History, Info, Lock, MapPin, Mic, Minus, Moon, Package, Palette, Pencil, PhoneOff, Plus, Power, RefreshCw, Route, Search,
  Star, Sun, Trash2, Unlink, Upload, UserRound, Video, Volume2, Watch, Wind, X,
} from 'lucide-react';

import {
  APP_BLUE, AppLogo, CALORIES, CAPACITY, OMICS_SECTIONS, RESULT_TITLES, SLEEP, STEPS, metricName,
} from './AppPhone';
import { BandArt, RingArt } from './DeviceArt';

// Every screen of the app the portal can draw, page by page, in the app's own
// layout and words. Spots a session can press carry a data-tap mark (see
// tapTargetOf in AppPhone.js); text a person typed themselves, and prices,
// are drawn as blank bars rather than made up.

// ---------------------------------------------------------------- the app's icons

function NavHome() {
  return <svg viewBox="0 0 16 18" aria-hidden="true"><path d="M1.5 16.5H5.25V10.25H10.75V16.5H14.5V6.75L8 1.875L1.5 6.75V16.5ZM0 18V6L8 0L16 6V18H9.25V11.75H6.75V18H0Z" /></svg>;
}
function NavInsights() {
  return (
    <svg viewBox="0 0 20 19" aria-hidden="true">
      <circle cx="16.9" cy="7.6" r="2.3" fill="none" strokeWidth="1.5" stroke="currentColor" />
      <circle cx="6.9" cy="3" r="2.3" fill="none" strokeWidth="1.5" stroke="currentColor" />
      <circle cx="10" cy="9.9" r="2.3" fill="none" strokeWidth="1.5" stroke="currentColor" />
      <circle cx="3" cy="16" r="2.3" fill="none" strokeWidth="1.5" stroke="currentColor" />
      <circle cx="16.9" cy="15.2" r="2.3" fill="none" strokeWidth="1.5" stroke="currentColor" />
      <path d="M8.1 5.1 9 7.7M12.2 9.2l2.4-.8M8.3 11.6l-3.6 3.2M11.9 11.3l3 2.4" fill="none" strokeWidth="1.4" stroke="currentColor" />
    </svg>
  );
}
function NavStore() {
  return <svg viewBox="0 0 16 20" aria-hidden="true"><path d="M0 20V4H4.25V3.75C4.25 2.7 4.6125 1.8125 5.3375 1.0875C6.0625 .3625 6.95 0 8 0C9.05 0 9.9375 .3625 10.6625 1.0875C11.3875 1.8125 11.75 2.7 11.75 3.75V4H16V20H0ZM1.5 18.5H14.5V5.5H11.75V8.5H10.25V5.5H5.75V8.5H4.25V5.5H1.5V18.5ZM5.75 4H10.25V3.75C10.25 3.11665 10.0333 2.58334 9.6 2.15C9.16665 1.71667 8.63335 1.5 8 1.5C7.36665 1.5 6.83335 1.71667 6.4 2.15C5.96665 2.58334 5.75 3.11665 5.75 3.75V4Z" /></svg>;
}
function NavProfile() {
  return <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M8 7.5C6.9 7.5 6 7.15 5.3 6.45C4.6 5.75 4.25 4.85 4.25 3.75C4.25 2.65 4.6 1.75 5.3 1.05C6 .35 6.9 0 8 0C9.1 0 10 .35 10.7 1.05C11.4 1.75 11.75 2.65 11.75 3.75C11.75 4.85 11.4 5.75 10.7 6.45C10 7.15 9.1 7.5 8 7.5ZM0 15.525V13.175C0 12.5417 .158 12 .475 11.55C.792 11.1 1.2 10.7583 1.7 10.525C2.817 10.025 3.8875 9.65 4.9125 9.4C5.9375 9.15 6.967 9.025 8 9.025C9.033 9.025 10.058 9.154 11.075 9.4125C12.092 9.671 13.158 10.043 14.273 10.53C14.795 10.765 15.213 11.107 15.528 11.554C15.843 12.001 16 12.542 16 13.175V15.525H0ZM1.5 14.025H14.5V13.175C14.5 12.908 14.421 12.654 14.2625 12.4125C14.104 12.171 13.908 11.992 13.675 11.875C12.608 11.358 11.633 11.004 10.75 10.8125C9.867 10.621 8.95 10.525 8 10.525C7.05 10.525 6.125 10.621 5.225 10.8125C4.325 11.004 3.35 11.358 2.3 11.875C2.067 11.992 1.875 12.171 1.725 12.4125C1.575 12.654 1.5 12.908 1.5 13.175V14.025ZM8 6C8.65 6 9.1875 5.7875 9.6125 5.3625C10.0375 4.9375 10.25 4.4 10.25 3.75C10.25 3.1 10.0375 2.5625 9.6125 2.1375C9.1875 1.7125 8.65 1.5 8 1.5C7.35 1.5 6.8125 1.7125 6.3875 2.1375C5.9625 2.5625 5.75 3.1 5.75 3.75C5.75 4.4 5.9625 4.9375 6.3875 5.3625C6.8125 5.7875 7.35 6 8 6Z" /></svg>;
}
function NavAI() {
  return (
    <svg viewBox="0 0 19 19" aria-hidden="true">
      <defs>
        <linearGradient id="xo-app-ai" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#30b0cd" /><stop offset=".5" stopColor="#2394cc" /><stop offset="1" stopColor="#2e74b2" />
        </linearGradient>
      </defs>
      <path fill="url(#xo-app-ai)" d="M16.16 6.3l-.21.5a.45.45 0 0 1-.4.27.45.45 0 0 1-.4-.27l-.22-.5a3.7 3.7 0 0 0-1.92-1.99l-.65-.3a.46.46 0 0 1 0-.85l.62-.28A3.75 3.75 0 0 0 14.92.82l.22-.54a.44.44 0 0 1 .81 0l.22.54a3.75 3.75 0 0 0 1.94 2.06l.62.28a.46.46 0 0 1 0 .85l-.66.3A3.7 3.7 0 0 0 16.16 6.3ZM6.91 1.77h3.45v1.77H6.91a5.24 5.24 0 0 0-5.18 5.3c0 3.19 2.13 5.27 6.91 7.5v-2.2h1.73a5.24 5.24 0 0 0 5.18-5.3h1.73a7 7 0 0 1-6.91 7.07V19C4.32 16.8 0 13.3 0 8.84A6.99 6.99 0 0 1 6.91 1.77Z" />
    </svg>
  );
}

// The ring and band marks the app puts on device rows, its own paths.
function RingIcon() {
  return (
    <svg className="xo-app-device-icon" viewBox="0 0 24 24" aria-hidden="true" fill="currentColor" stroke="currentColor" strokeWidth=".6">
      <path strokeWidth="0" d="M14.501 14C13.0562 16.5283 8.501 17.9999 6.00098 18.9999C7.50098 19.9999 10.601 22 11.001 22C15.001 22 16.5007 19.4999 20.0007 16C21.8645 14.1363 21.1676 7.8333 20.001 5.49997L16.001 2.99997C16.501 4.49997 16.501 10.5 14.501 14Z" />
      <path d="M19.4065 15.6071C20.9906 12.9838 21.286 10.8114 21.1684 9.22888C21.0501 7.6373 20.5192 6.63657 20.3882 6.28071L20.9961 6.05716C21.104 6.35012 21.6872 7.47207 21.8143 9.1807C21.942 10.8987 21.6138 13.2048 19.961 15.9419C18.7953 17.8723 17.4407 19.2645 16.2454 20.2008C15.0609 21.1287 14.0075 21.6283 13.4352 21.7397L13.3113 21.1038C13.7392 21.0205 14.6973 20.5908 15.8459 19.691C16.9838 18.7996 18.2843 17.4654 19.4065 15.6071Z" />
      <path d="M3.58817 18.3395C3.53345 18.4089 8.73526 21.4515 8.88881 21.5778C10.1558 22.3428 11.8034 22.3979 13.5285 21.7322C13.703 21.6652 13.7893 21.47 13.7218 21.2963C13.655 21.123 13.4591 21.0362 13.2858 21.103C11.738 21.7003 10.3378 21.6646 9.2376 21.0003L6.31083 19.2329C12.8288 19.026 18.9284 8.92654 16.0761 3.06137L19.0036 4.82916C19.618 5.20015 20.1043 5.74897 20.4478 6.46082C20.5279 6.62839 20.7308 6.69868 20.8988 6.61787C21.0668 6.53706 21.1366 6.33492 21.0558 6.1669C20.6545 5.33543 20.082 4.69119 19.3535 4.25131L14.4444 1.28691C7.04897 -2.63602 -2.63381 13.2523 3.58846 18.3396L3.58817 18.3395ZM15.8821 4.4447C17.2025 9.68623 12.6986 17.1447 7.4451 18.4165C6.00679 13.4713 10.8374 5.47339 15.8821 4.4447ZM13.8319 1.72303C14.6883 2.13376 15.3124 2.91945 15.6697 3.78643C10.3025 4.96413 5.29732 13.2349 6.77331 18.5338C5.83032 18.6255 4.84154 18.4372 4.07278 17.8637C-1.5852 13.3348 7.18101 -1.18773 13.8319 1.72303Z" />
    </svg>
  );
}
function BandIcon() {
  return (
    <svg className="xo-app-device-icon" viewBox="0 0 24 24" aria-hidden="true" fill="currentColor" stroke="currentColor" strokeWidth=".5">
      <path strokeWidth="1" d="M20.001 12.5C20.001 15.4878 19.5599 17.5232 18.8857 18.9102C18.8694 18.9139 18.8502 18.9205 18.8271 18.9248C18.6442 18.9588 18.2775 19 17.5918 19C15.4251 19 14.8478 18.8825 14.2852 18.6621C13.6709 18.4215 13.2914 18.1548 13.0654 17.9502C13.4688 16.5495 13.6904 14.7574 13.6904 12.5C13.6904 10.2411 13.4693 8.44809 13.0654 7.04689C13.2903 6.84392 13.6697 6.57941 14.2861 6.33791C14.8482 6.11763 15.4259 6.00002 17.5928 6.00002C18.2788 6.00002 18.6452 6.04128 18.8281 6.07521C18.8506 6.07939 18.8696 6.08427 18.8857 6.08791C19.5602 7.47519 20.0009 9.51094 20.001 12.5Z" />
      <path d="M18.5006 20.5432C18.1756 20.595 17.7641 20.6209 17.2603 20.6209C15.2817 20.6209 14.4733 20.5097 13.6575 20.1247C13.1003 19.8619 12.7006 19.572 12.4161 19.3073C12.1367 20.1499 11.7952 20.8092 11.4061 21.3113C10.3423 20.0221 9.52691 17.4275 9.52691 12.5C9.52691 7.5725 10.3427 4.97797 11.4061 3.68902C11.7952 4.1911 12.1363 4.85037 12.4161 5.69297C12.701 5.42799 13.1007 5.13808 13.6579 4.87555C14.4737 4.49079 15.2821 4.37933 17.2607 4.37933C17.7645 4.37933 18.176 4.40524 18.501 4.45706C17.1161 1.82562 14.9398 1.50002 13.0126 1.50002H9.15712C6.03333 1.50002 3.50098 4.07475 3.50098 12.5C3.50098 20.9253 6.03333 23.5 9.15712 23.5H13.0126C14.9398 23.5 17.1161 23.1744 18.5006 20.5432ZM4.1938 12.5C4.1938 4.7125 6.2196 2.33284 8.71856 2.33284C9.55167 2.33284 10.3321 2.59782 11.0025 3.23924C9.54774 4.75699 8.83409 7.82428 8.83409 12.5C8.83409 17.1757 9.54813 20.243 11.0025 21.7608C10.3321 22.402 9.55167 22.6672 8.71856 22.6672C6.2196 22.6672 4.1938 20.2875 4.1938 12.5Z" />
    </svg>
  );
}

// The globe that turns behind the Longevity AI welcome, drawn as lines.
function Globe3D() {
  return (
    <svg className="xo-app-globe" viewBox="0 0 200 200" aria-hidden="true">
      <circle cx="100" cy="100" r="92" />
      <ellipse cx="100" cy="100" rx="40" ry="92" />
      <ellipse cx="100" cy="100" rx="72" ry="92" />
      <ellipse cx="100" cy="100" rx="92" ry="34" />
      <ellipse cx="100" cy="100" rx="92" ry="66" />
      <path d="M8 100h184M100 8v184" />
    </svg>
  );
}

// ---------------------------------------------------------------- shared pieces

// "Add a *Phenome*Tech Device": the app marks the bold part with stars.
function highlighted(text) {
  return text.split('*').map((piece, index) => (index % 2 === 1 ? <b key={index}>{piece}</b> : <React.Fragment key={index}>{piece}</React.Fragment>));
}

function dayLabel(at, offset) {
  return new Date(at + offset * 86400000).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric' });
}

function AppBar() {
  return <div className="xo-app-bar"><AppLogo /></div>;
}

// The bar over a page pushed onto a tab: back, the page's title, and whatever
// sits on its right.
function PageBar({ title, back: Back = ChevronLeft, action = null, subtitle = null }) {
  return (
    <div className="xo-app-backbar">
      <span className="xo-app-back" data-tap="back"><Back aria-hidden="true" /></span>
      <strong>{title}{subtitle && <small>{subtitle}</small>}</strong>
      <span className="xo-app-bar-action">{action}</span>
    </div>
  );
}

// A page's content. The phone scrolls it, never the person.
function Body({ className = '', children }) {
  return <div className={`xo-app-body ${className}`}>{children}</div>;
}

// Text a person wrote themselves, or a price: a blank bar of about that length.
function Blank({ width = 90, light = false }) {
  return <i className={`xo-app-blank${light ? ' is-light' : ''}`} style={{ width }} aria-hidden="true" />;
}

const TABS = [['home', NavHome, 'navbar.home'], ['insights', NavInsights, 'navbar.insights'], ['store', NavStore, 'navbar.store'], ['profile', NavProfile, 'navbar.profile']];

function BottomNav({ active, as }) {
  return (
    <div className="xo-app-nav">
      <div className="xo-app-nav-pill">
        {TABS.map(([key, Icon, label]) => (
          <span key={key} className={active === key ? 'is-active' : ''} data-tap={`nav.${key}`}><Icon /><b>{as(label)}</b></span>
        ))}
      </div>
      <span className={`xo-app-nav-ai${active === 'ai' ? ' is-active' : ''}`} data-tap="nav.ai"><NavAI /></span>
    </div>
  );
}

function Ring({ value, max, color, size = 84, stroke = 9, children }) {
  const radius = (size - stroke) / 2;
  const length = 2 * Math.PI * radius;
  const share = Math.max(0, Math.min(1, value / max));
  return (
    <span className="xo-app-ring" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#e5e5e6" strokeWidth={stroke} />
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={color} strokeWidth={stroke} strokeLinecap="round"
          strokeDasharray={`${share * length} ${length}`} transform={`rotate(-90 ${size / 2} ${size / 2})`} />
      </svg>
      <span style={{ color }}>{children}</span>
    </span>
  );
}

// D, W, M, 6M, Y: the range picker of every tracker. The app names the six
// months sixMonth in some trackers and sixMonths in others.
const RANGES = [['day', 'nutrition.range_d'], ['week', 'nutrition.range_w'], ['month', 'nutrition.range_m'], ['sixMonth', 'nutrition.range_6m'], ['year', 'nutrition.range_y']];

function RangeTabs({ as }) {
  return (
    <div className="xo-app-range">
      {RANGES.map(([value, key], index) => (
        <span key={value} className={index === 0 ? 'is-active' : ''} data-tap={value === 'sixMonth' ? 'range.sixMonth range.sixMonths' : `range.${value}`}>{as(key)}</span>
      ))}
    </div>
  );
}

function DateNav({ label }) {
  return (
    <div className="xo-app-datenav">
      <span data-tap="range.back"><ChevronLeft aria-hidden="true" /></span>
      <strong>{label}</strong>
      <span data-tap="range.forward" className="is-off"><ChevronRight aria-hidden="true" /></span>
    </div>
  );
}

// A card lit from above in one colour and fading into the app's light blue,
// the way the app draws its journal, omics and discover cards.
function Glow({ tone, tap, className = '', children }) {
  return <div className={`xo-app-glow is-${tone} ${className}`} data-tap={tap}>{children}</div>;
}

function Bar({ share, tone }) {
  return <i className={`xo-app-bar-track is-${tone}`}><b style={{ width: `${Math.round(share * 100)}%` }} /></i>;
}

function Field({ label, children, tap }) {
  return (
    <label className="xo-app-field" data-tap={tap}>
      <small>{label}</small>
      <span>{children}</span>
    </label>
  );
}

// ---------------------------------------------------------------- Home

const HOME_MONITOR = [
  ['steps', Footprints, 'homepage.step_count', '6,412', ''],
  ['calories', Flame, 'homepage.calories_label', '420', 'activity.kcal'],
  ['distance', Route, 'homepage.step_distance', '4.6', 'km'],
  ['respiratoryRate', Wind, 'homepage.respiratory_rate', '14', 'rpm'],
  ['bloodOxygen', Droplet, 'homepage.blood_oxygen', '98%', ''],
  ['bloodPressure', Gauge, 'homepage.blood_pressure', '118/76', 'mmHg'],
  ['restingHeartRate', HeartPulse, 'homepage.resting_heart_rate', '58', 'health.bpm'],
  ['heartRateAvg', Heart, 'homepage.heart_rate_label', '72', 'health.bpm'],
  ['hrv', Activity, 'health.variability_heart_rate', '46', 'ms'],
  ['sleep', Moon, 'homepage.hours_of_sleep', '7h 32m', ''],
];
const unitOf = (unit, as) => (unit.includes('.') ? as(unit) : unit);
const DEVICE_NAMES = { ring: 'Phenome Ring', band: 'Phenome Band' };

function HomeScreen({ as, at, hints }) {
  return (
    <Body className="is-home">
      {hints.hasDevice ? (
        <div className="xo-app-device-row">
          {hints.kinds.slice(0, 2).map((kind, index) => (
            <span key={kind} className="xo-app-tile" data-tap={index === 0 ? 'home.device.tile' : undefined}>
              {kind === 'band' ? <BandIcon /> : <RingIcon />}
              <b>{DEVICE_NAMES[kind]}</b>
              {index === 0 ? <Star className="xo-app-star" aria-hidden="true" /> : <i />}
            </span>
          ))}
          <span className="xo-app-tile is-all" data-tap="home.device.all"><b>{as('device.all_your_devices')}</b><ChevronRight aria-hidden="true" /></span>
        </div>
      ) : (
        <div className="xo-app-banner" data-tap="home.device.add">
          <span>{highlighted(as('homepage.add_tech_device'))}</span>
          <CirclePlus aria-hidden="true" />
        </div>
      )}
      <div className="xo-app-days">
        <span data-tap="home.day.back"><ChevronLeft aria-hidden="true" /></span>
        <span>{dayLabel(at, -1)}</span><strong>{as('homepage.today')}</strong><span>{dayLabel(at, 1)}</span>
        <span data-tap="home.day.forward"><ChevronRight aria-hidden="true" /></span>
      </div>
      <div className="xo-app-rings">
        <span data-tap="home.sleep"><Ring value={86} max={100} color={SLEEP}>86</Ring><em>{as('homepage.sleep_label')} ›</em></span>
        <span data-tap="home.calories"><Ring value={420} max={600} color={CALORIES}><small>420</small><small>{as('activity.kcal')}</small></Ring><em>{as('homepage.calories_label')} ›</em></span>
        <span data-tap="home.steps"><Ring value={6.4} max={8} color={STEPS}>6.4k</Ring><em>{as('homepage.steps_label')} ›</em></span>
      </div>
      <Glow tone="capacity" tap="home.capacity" className="xo-app-capacity">
        <Ring value={7} max={10} color="#fff" size={92} stroke={10}>7</Ring>
        <span><strong>{as('homepage.capacity')}</strong><small>{as('homepage.capacity_stable')}</small></span>
        <ChevronRight aria-hidden="true" />
      </Glow>
      <h3 className="xo-app-heading">{as('homepage.health_journal')}</h3>
      <Glow tone="activity" tap="home.activity" className="xo-app-journal">
        <span><strong>{as('homepage.activity')}</strong><small>{as('homepage.activity_subtitle')}</small></span>
        <span data-tap="home.activity.add"><CirclePlus aria-hidden="true" /></span>
      </Glow>
      <div className="xo-app-pair">
        <Glow tone="diet" tap="home.diet" className="xo-app-journal is-small">
          <strong>{as('homepage.diet_label')}<ChevronRight aria-hidden="true" /></strong><small>{as('homepage.diet_subtitle')}</small>
        </Glow>
        <Glow tone="habit" tap="home.habits" className="xo-app-journal is-small">
          <strong>{as('homepage.habits_label')}<ChevronRight aria-hidden="true" /></strong><small>{as('homepage.habits_subtitle_short')}</small>
        </Glow>
      </div>
      <h3 className="xo-app-heading">{as('homepage.health_monitor_title')}</h3>
      <div className="xo-app-rows">
        {HOME_MONITOR.map(([key, Icon, label, value, unit]) => (
          <span key={key} className="xo-app-tile xo-app-metric-row" data-tap={`home.metric.${key}`}>
            <Icon aria-hidden="true" />
            <b>{as(label)}</b>
            <em>{value}{unit && <small> {unitOf(unit, as)}</small>}</em>
            <i className="xo-app-dot" />
          </span>
        ))}
      </div>
      <h3 className="xo-app-heading">{as('homepage.discover')}</h3>
      <Glow tone="consult" tap="home.consult" className="xo-app-discover">
        <span><strong>{as('homepage.consultations_title')}</strong><small>{as('homepage.consultations_subtitle')}</small></span>
        <b className="xo-app-pill" data-tap="home.consult.book"><CirclePlus aria-hidden="true" />{as('homepage.book')}</b>
      </Glow>
      <Glow tone="games" tap="home.games" className="xo-app-discover">
        <span><strong>{as('homepage.games_title')}</strong><small>{as('homepage.games_subtitle')}</small></span>
        <ChevronRight aria-hidden="true" />
      </Glow>
      <div className="xo-app-soon"><strong>{as('homepage.personalised_supplements')}</strong><small>{as('homepage.supplements_subtitle')}</small></div>
      <div className="xo-app-soon"><strong>{as('homepage.glucose_monitoring')}</strong><small>{as('homepage.coming_soon')}</small></div>
    </Body>
  );
}

// ---------------------------------------------------------------- Insights, results and the omics sections

const PORTFOLIO = [
  ['genome', 'omics.genome.title', 'omics.genome.subtitle'], ['oral', 'omics.oral.title', 'omics.oral.subtitle'],
  ['gut', 'omics.gut.title', 'omics.gut.subtitle'], ['proteome', 'omics.proteome.title', 'omics.proteome.subtitle'],
  ['transcriptome', 'omics.transcriptome.title', 'omics.transcriptome.subtitle'],
  ['metabolome', 'omics.metabolome.title', 'omics.metabolome.subtitle'], ['lipidome', 'omics.lipidome.title', 'omics.lipidome.subtitle'],
];
const RESULTS = [['oncology', 'omics.oncology.title', 'omics.oncology.subtitle'], ['infectious', 'omics.infectious.title', 'omics.infectious.subtitle']];

function OmicsTile({ as, tone, tap, title, subtitle }) {
  return (
    <Glow tone={tone} tap={tap} className="xo-app-omics-tile">
      <span><strong>{as(title)}</strong><small>{as(subtitle)}</small></span>
      <ChevronRight aria-hidden="true" />
    </Glow>
  );
}

function InsightsScreen({ as }) {
  return (
    <Body>
      <h1 className="xo-app-title">{as('omics.title')}</h1>
      <div className="xo-app-gradient-button is-wide" data-tap="insights.reports"><span>{as('omics.view_reports')}</span><ChevronRight aria-hidden="true" /></div>
      <OmicsTile as={as} tone="clinical" tap="insights.clinical" title="omics.clinical.title" subtitle="omics.clinical.subtitle" />
      <h3 className="xo-app-heading">{as('omics.portfolio_title')}</h3>
      {PORTFOLIO.map(([key, title, subtitle]) => <OmicsTile key={key} as={as} tone="omics" tap={`insights.${key}`} title={title} subtitle={subtitle} />)}
      <h3 className="xo-app-heading">{as('omics.results_title')}</h3>
      {RESULTS.map(([key, title, subtitle]) => <OmicsTile key={key} as={as} tone="results" tap={`insights.${key}`} title={title} subtitle={subtitle} />)}
    </Body>
  );
}

// One category's results, one report at a time, before its section opens.
function ResultsScreen({ as, section }) {
  return (
    <>
      <PageBar title="" back={ArrowLeft} />
      <Body className="is-centered">
        <h2 className="xo-app-results-title">{as(RESULT_TITLES[section] || 'omics.results_title')}</h2>
        <div className="xo-app-results-carousel">
          <span data-tap="results.prev"><ChevronLeft aria-hidden="true" /></span>
          <i><AppLogo height={52} /></i>
          <span data-tap="results.next"><ChevronRight aria-hidden="true" /></span>
        </div>
        <div className="xo-app-dots"><i className="is-on" /><i /></div>
        <div className="xo-app-results-card">
          <span><small>{as('omics.report.kit_id')}</small><Blank width={120} /></span>
          <span><small>{as('omics.report.status')}</small><em className="xo-app-status is-done">{as('omics.status.validated')}</em></span>
        </div>
        <div className="xo-app-dark-button" data-tap="results.view">{as('omics.report.view_report')}<ArrowRight aria-hidden="true" /></div>
      </Body>
    </>
  );
}

function OmicsScreen({ as, section }) {
  const shown = OMICS_SECTIONS[section] || { title: 'omics.title', subtitle: null, parts: [] };
  return (
    <>
      <PageBar title={as(shown.title)} action={<span data-tap="omics.pdf"><FileText aria-hidden="true" /></span>} />
      <Body>
        {shown.subtitle && <p className="xo-app-subtitle">{as(shown.subtitle)}</p>}
        {shown.parts.map((part) => (
          <div key={part} className="xo-app-tile xo-app-omics-part" data-tap={part}>
            <span className="xo-app-card-icon"><NavInsights /></span>
            <strong>{as(part)}</strong>
            <ChevronRight aria-hidden="true" />
          </div>
        ))}
        <div className="xo-app-gradient-button" data-tap="omics.pdf"><FileText aria-hidden="true" />{as('common.generate_pdf')}</div>
      </Body>
    </>
  );
}

function ClinicalScreen({ as }) {
  return (
    <>
      <PageBar title={as('omics.clinical.title')} />
      <Body>
        <div className="xo-app-gradient-button" data-tap="clinical.upload"><Upload aria-hidden="true" />{as('omics.clinical.ai_upload')}</div>
        <div className="xo-app-tile xo-app-omics-part" data-tap="clinical.values">
          <span className="xo-app-card-icon"><UserRound aria-hidden="true" /></span>
          <span><strong>{as('omics.clinical.section_personal')}</strong><small>{as('omics.clinical.subtitle')}</small></span>
          <ChevronRight aria-hidden="true" />
        </div>
      </Body>
    </>
  );
}

// ---------------------------------------------------------------- reports

const PDF_LANGUAGES = ['en', 'sv', 'tr', 'de'];

function languageName(code) {
  try {
    return new Intl.DisplayNames([code], { type: 'language' }).of(code) || code;
  } catch {
    return code;
  }
}

function ReportsScreen({ as, part }) {
  if (part === 'request') {
    return (
      <>
        <PageBar title={as('common.generate_pdf')} />
        <Body>
          <div className="xo-app-sheet-card">
            <strong>{as('pdf.select_language')}</strong>
            <small>{as('pdf.choose_language')}</small>
            {PDF_LANGUAGES.map((code, index) => (
              <span key={code} className={`xo-app-choice${index === 0 ? ' is-picked' : ''}`} data-tap={index === 0 ? 'reports.language' : undefined}>
                <Globe aria-hidden="true" />{languageName(code)}<i />
              </span>
            ))}
            <div className="xo-app-two-buttons">
              <span className="xo-app-outline-button">{as('pdf.cancel')}</span>
              <span className="xo-app-gradient-button" data-tap="reports.generate">{as('pdf.generate')}</span>
            </div>
          </div>
        </Body>
      </>
    );
  }
  if (part === 'viewer') {
    return (
      <>
        <PageBar title={as('reports.type.health_pdf')} />
        <Body className="is-viewer">
          <div className="xo-app-pdf-page"><AppLogo height={28} /><Blank width="70%" /><Blank width="90%" /><Blank width="84%" /><Blank width="60%" /><i className="xo-app-pdf-chart" /><Blank width="88%" /><Blank width="76%" /></div>
          <span className="xo-app-pdf-count" data-tap="reports.pages">1 {as('pdf_viewer.of')} 12</span>
        </Body>
      </>
    );
  }
  return (
    <>
      <PageBar title={as('reports.title')} subtitle={`1 ${as('reports.new_available')}`} />
      <Body>
        <div className="xo-app-tile xo-app-reports-head">
          <FolderOpen aria-hidden="true" />
          <span><strong>{as('reports.header.title')}</strong><small>{as('reports.header.subtitle')}</small></span>
        </div>
        <h3 className="xo-app-heading is-small">{as('reports.available')} (2)</h3>
        {['omics.genome.title', 'omics.gut.title'].map((title, index) => (
          <div key={title} className="xo-app-tile xo-app-report" data-tap={index === 0 ? 'reports.open' : undefined}>
            <span className="xo-app-report-icon"><FileText aria-hidden="true" /></span>
            <span><strong>{as(title)}</strong><small>EN · {as('reports.type.health_pdf')}</small><small><Clock aria-hidden="true" />{dayLabel(Date.now(), -index * 6)}</small></span>
            {index === 0 && <em className="xo-app-badge">{as('reports.badge.new')}</em>}
            <ChevronRight aria-hidden="true" />
          </div>
        ))}
      </Body>
    </>
  );
}

// ---------------------------------------------------------------- Longevity AI

function AiChat({ as, chatting, marked = true }) {
  const tap = (key) => (marked ? key : undefined);
  return (
    <div className="xo-app-ai">
      <div className="xo-app-ai-top">
        <span data-tap={tap('ai.new')}><Plus aria-hidden="true" /></span>
        <span data-tap={tap('ai.history')}><History aria-hidden="true" /></span>
      </div>
      {chatting ? (
        <div className="xo-app-chat">
          <p className="xo-app-bubble is-me"><Blank width={170} light /></p>
          <p className="xo-app-bubble"><i /><i /><i /></p>
          <small>{as('ai.typing')}</small>
        </div>
      ) : (
        <div className="xo-app-ai-welcome">
          <Globe3D />
          <h1>{as('ai.welcome')}</h1>
          <p>{as('ai.welcome_subtitle')}</p>
          <div className="xo-app-prompts">
            {[1, 2, 3, 4].map((index) => <span key={index} data-tap={tap(`ai.prompt.${index}`)}>{as(`ai.prompt_${index}`)}</span>)}
          </div>
        </div>
      )}
      <div className="xo-app-ask">
        <span data-tap={tap('ai.ask')}>{as('ai.ask_hint')}</span>
        <span data-tap={tap('ai.voice')}><Mic aria-hidden="true" /></span>
        <b data-tap={tap('ai.send')}><ArrowUp aria-hidden="true" /></b>
      </div>
    </div>
  );
}

function AiScreen({ as, part, chatting }) {
  if (part === 'voice') {
    return (
      <div className="xo-app-voice">
        <Globe3D />
        <p><Blank width={220} light /><Blank width={160} light /></p>
        <div className="xo-app-voice-controls">
          <span data-tap="ai.voice.speaker"><Volume2 aria-hidden="true" /></span>
          <span className="is-close" data-tap="ai.voice.close"><X aria-hidden="true" /></span>
          <span data-tap="ai.voice.mic"><Mic aria-hidden="true" /></span>
        </div>
      </div>
    );
  }
  // The history is a sheet over the chat (see AiHistorySheet); the chat under it takes no presses.
  return <AiChat as={as} chatting={chatting && part !== 'history'} marked={part !== 'history'} />;
}

function AiHistorySheet({ as }) {
  return (
    <>
      <div className="xo-app-sheet-head"><strong>{as('ai.history.title')}</strong><span className="xo-app-pill is-dark" data-tap="ai.new"><Plus aria-hidden="true" />{as('ai.history.new_chat')}</span></div>
      {[as('common.today'), as('habits.yesterday')].map((when, group) => (
        <div key={when} className="xo-app-history-group">
          <small>{when}</small>
          {[0, 1].map((index) => (
            <span key={index} className="xo-app-history-row" data-tap={group === 0 && index === 0 ? 'ai.history.row' : undefined}>
              <Blank width={200 - index * 40} /><ChevronRight aria-hidden="true" />
            </span>
          ))}
        </div>
      ))}
    </>
  );
}

// ---------------------------------------------------------------- the store

function Kit({ index = 0 }) {
  return <span className={`xo-app-kit is-${index % 4}`}><AppLogo color="#fff" height={22} /></span>;
}

function CartBadge({ count }) {
  return <span className="xo-app-cart-icon" data-tap="store.cart"><NavStore />{count > 0 && <b>{count}</b>}</span>;
}

function StoreScreen({ as, part, added, hints }) {
  const products = ['omics.genome.title', 'omics.gut.title', 'omics.oral.title', 'omics.metabolome.title'];
  if (part === 'product') {
    return (
      <>
        <PageBar title="" back={ArrowLeft} action={<CartBadge count={hints.cart} />} />
        <Body className="is-product">
          <div className="xo-app-product-image" data-tap="store.image"><Kit /></div>
          <div className="xo-app-dots"><i className="is-on" /><i /><i /></div>
          <small className="xo-app-muted">{as('store.product_category')}</small>
          <h2 className="xo-app-product-title">{as(products[0])}</h2>
          <Blank width={80} />
          <div className="xo-app-description"><Blank width="92%" /><Blank width="86%" /><Blank width="74%" /></div>
        </Body>
        <div className="xo-app-buy-bar">
          <span className="xo-app-qty"><span data-tap="store.minus"><Minus aria-hidden="true" /></span><b>1</b><span><Plus aria-hidden="true" /></span></span>
          <span className={`xo-app-primary${added ? ' is-done' : ''}`} data-tap="store.add">
            {added ? <><Check aria-hidden="true" />{as('store.added_to_cart')}</> : as('store.add_to_cart')}
          </span>
        </div>
      </>
    );
  }
  if (part === 'cart') return <CartPage as={as} />;
  return (
    <Body>
      <h1 className="xo-app-title">{as('store.title')}</h1>
      <div className="xo-app-search is-store" data-tap="store.search"><span>{as('store.search_hint')}</span><Search aria-hidden="true" /></div>
      <div className="xo-app-tabs">
        <span className="is-active" data-tap="store.category.all">{as('store.all_products')}</span>
        <span data-tap="store.category">{as('omics.genome.title')}</span>
        <span>{as('omics.gut.title')}</span>
      </div>
      <div className="xo-app-products">
        {products.map((title, index) => (
          <div key={title} className="xo-app-tile xo-app-product" data-tap={index === 0 ? 'store.product' : undefined}>
            <Kit index={index} />
            <strong>{as(title)}</strong>
            <Blank width={54} />
          </div>
        ))}
      </div>
      {hints.cart > 0 && (
        <span className="xo-app-cart-float" data-tap="store.cart"><NavStore />{as('store.your_cart')}<b>{hints.cart}</b></span>
      )}
    </Body>
  );
}

function CartPage({ as }) {
  return (
    <>
      <PageBar title={as('store.your_cart')} back={ArrowLeft} />
      <Body>
        <div className="xo-app-tile xo-app-cart-item">
          <Kit />
          <span><strong>{as('omics.genome.title')}</strong><small><Blank width={60} /> {as('store.each')}</small>
            <span className="xo-app-qty is-small"><span data-tap="cart.minus"><Minus aria-hidden="true" /></span><b>1</b><span data-tap="cart.plus"><Plus aria-hidden="true" /></span></span>
          </span>
          <span className="xo-app-cart-remove" data-tap="cart.remove"><Trash2 aria-hidden="true" /></span>
        </div>
      </Body>
      <div className="xo-app-total">
        <span><small>{as('store.total')} (1 {as('store.item')})</small><Blank width={90} /></span>
        <span className="xo-app-primary" data-tap="cart.checkout">{as('store.proceed_to_checkout')}</span>
      </div>
    </>
  );
}

// The delivery address is a sheet over the cart.
function AddressSheet({ as, paid }) {
  return (
    <>
      <div className="xo-app-sheet-head"><strong>{as('store.delivery_address')}</strong></div>
      <div className="xo-app-search" data-tap="checkout.address"><Search aria-hidden="true" /><span>{as('profile.address.line1_hint')}</span></div>
      <small className="xo-app-muted">{as('store.address.search_help')}</small>
      {['store.field.name', 'store.field.address1', 'store.field.city', 'store.field.postcode'].map((field) => (
        <Field key={field} label={as(field)}><Blank width={140} /></Field>
      ))}
      <span className={`xo-app-primary${paid ? ' is-done' : ''}`} data-tap="checkout.continue">{paid && <Check aria-hidden="true" />}{as('store.continue_to_payment')}</span>
    </>
  );
}

function OrdersScreen({ as }) {
  return (
    <>
      <PageBar title={as('profile.orders.title')} />
      <Body>
        {['profile.orders.status.delivered', 'profile.orders.status.dispatched'].map((status, index) => (
          <div key={status} className="xo-app-tile xo-app-order" data-tap={index === 0 ? 'orders.detail' : undefined}>
            <Package aria-hidden="true" />
            <span><strong>{as(index === 0 ? 'omics.genome.title' : 'omics.gut.title')}</strong><small>{as('profile.orders.placed')} {dayLabel(Date.now(), -9 - index * 20)}</small></span>
            <em className={`xo-app-status${index === 0 ? ' is-done' : ''}`}>{as(status)}</em>
          </div>
        ))}
      </Body>
    </>
  );
}

function TestsScreen({ as }) {
  return (
    <>
      <PageBar title={as('profile.tests.title')} />
      <Body>
        {['omics.gut.title', 'omics.oral.title'].map((title, index) => (
          <div key={title} className="xo-app-tile xo-app-order" data-tap={index === 0 ? 'tests.tracking' : undefined}>
            <NavInsights />
            <span><strong>{as(title)}</strong><small>{as('profile.tests.kit_id')} <Blank width={70} /></small></span>
            <ChevronRight aria-hidden="true" />
          </div>
        ))}
      </Body>
    </>
  );
}

// ---------------------------------------------------------------- the profile

const PROFILE_SECTIONS = [
  ['profile.section.personal', [[Heart, 'profile.menu.health_history'], [Dumbbell, 'profile.menu.lifestyle']]],
  ['profile.section.order', [[Package, 'profile.menu.order_history'], [History, 'profile.menu.past_tests'], [CircleCheck, 'profile.menu.activate_test']]],
  ['profile.section.security', [[Lock, 'profile.menu.change_password'], [MapPin, 'profile.menu.saved_address'], [Info, 'profile.menu.faqs'], [Eye, 'profile.menu.privacy']]],
  ['profile.section.general', [[Palette, 'profile.menu.appearance'], [Globe, 'profile.menu.language']]],
];

// The forms behind the profile's rows: their fields, in the app's words.
const PROFILE_FORMS = {
  personal_details: ['profile.details.title', ['profile.details.firstname', 'profile.details.lastname', 'profile.details.email', 'profile.details.sex', 'profile.details.dob', 'profile.details.weight', 'profile.details.height']],
  health_history: ['profile.health.title', ['profile.health.smoking', 'profile.health.alcohol', 'profile.health.chronic', 'profile.health.medical', 'profile.health.medications']],
  lifestyle: ['profile.lifestyle.title', ['profile.lifestyle.activity', 'profile.lifestyle.water', 'profile.lifestyle.sleep', 'profile.lifestyle.goals']],
  change_password: ['profile.password.title', ['profile.password.current', 'profile.password.new', 'profile.password.confirm']],
  saved_address: ['profile.address.title', ['profile.address.line1', 'profile.address.town', 'profile.address.city', 'profile.address.postal_code', 'profile.address.country']],
};
const APP_LANGUAGES = ['en', 'sv', 'tr', 'de', 'es', 'fr'];

function ProfileScreen({ as, person, part, hints }) {
  if (PROFILE_FORMS[part]) {
    const [title, fields] = PROFILE_FORMS[part];
    const secret = part === 'change_password';
    return (
      <>
        <PageBar title={as(title)} />
        <Body>
          {part === 'saved_address' && <div className="xo-app-search" data-tap="profile.address"><Search aria-hidden="true" /><span>{as('profile.address.line1_hint')}</span></div>}
          {fields.map((field) => (
            <Field key={field} label={as(field)}>
              {secret ? '••••••••' : field === 'profile.details.email' && person.email ? person.email : <Blank width={120} />}
            </Field>
          ))}
          <span className="xo-app-primary" data-tap="profile.save">{as(secret ? 'profile.password.button' : 'common.save')}</span>
          {part === 'personal_details' && <span className="xo-app-link is-danger" data-tap="profile.delete">{as('profile.details.deactivate')}</span>}
        </Body>
      </>
    );
  }
  if (part === 'appearance') {
    const dark = hints.appearance === 'dark';
    return (
      <>
        <PageBar title={as('profile.appearance.title')} />
        <Body>
          <p className="xo-app-subtitle">{as('profile.appearance.description')}</p>
          {[['light', Sun, 'profile.appearance.light', 'profile.appearance.light_desc'], ['dark', Moon, 'profile.appearance.dark', 'profile.appearance.dark_desc']].map(([mode, Icon, name, detail]) => (
            <span key={mode} className={`xo-app-choice is-big${(mode === 'dark') === dark ? ' is-picked' : ''}`} data-tap={`profile.appearance.${mode}`}>
              <Icon aria-hidden="true" /><span><strong>{as(name)}</strong><small>{as(detail)}</small></span><i />
            </span>
          ))}
        </Body>
      </>
    );
  }
  if (part === 'language') {
    const picked = hints.language ? hints.language.toLowerCase() : 'en';
    const shown = APP_LANGUAGES.includes(picked) ? APP_LANGUAGES : [picked, ...APP_LANGUAGES.slice(0, 5)];
    return (
      <>
        <PageBar title={as('profile.language.title')} />
        <Body>
          {shown.map((code, index) => (
            <span key={code} className={`xo-app-choice${code === picked ? ' is-picked' : ''}`} data-tap={`profile.language.${code}${index === 0 ? ' profile.language.any' : ''}`}>
              <Globe aria-hidden="true" />{languageName(code)}<i />
            </span>
          ))}
        </Body>
      </>
    );
  }
  if (part === 'activate_test') {
    return (
      <>
        <PageBar title={as('profile.activate.title')} />
        <Body>
          <h2 className="xo-app-page-title">{as('profile.activate.header')}</h2>
          <p className="xo-app-subtitle">{as('profile.activate.description')}</p>
          <Field label={as('profile.activate.code_label')}><Blank width={150} /></Field>
          <span className="xo-app-primary" data-tap="profile.activate">{as('profile.activate.button')}</span>
        </Body>
      </>
    );
  }
  if (part === 'faqs') {
    return (
      <>
        <PageBar title={as('profile.faqs.title')} />
        <Body>
          {['profile.faqs.q1', 'profile.faqs.q2', 'profile.faqs.q3'].map((question) => (
            <span key={question} className="xo-app-tile xo-app-faq"><strong>{as(question)}</strong><ChevronDown aria-hidden="true" /></span>
          ))}
        </Body>
      </>
    );
  }
  if (part === 'privacy') {
    return (
      <>
        <PageBar title={as('profile.menu.privacy')} />
        <Body><div className="xo-app-description"><Blank width="94%" /><Blank width="88%" /><Blank width="90%" /><Blank width="72%" /><Blank width="85%" /><Blank width="64%" /></div></Body>
      </>
    );
  }
  return (
    <Body>
      <h1 className="xo-app-title is-centered">{as('profile.my_account')}</h1>
      <div className="xo-app-person" data-tap="profile.card">
        <span>{person.initials}</span>
        <span><strong>{person.name}</strong><small>{person.email}</small></span>
        <ArrowRight aria-hidden="true" />
      </div>
      {PROFILE_SECTIONS.map(([title, rows]) => (
        <div key={title} className="xo-app-menu">
          <h4>{as(title)}</h4>
          <div>
            {rows.map(([Icon, row]) => <span key={row} data-tap={row}><Icon aria-hidden="true" />{as(row)}<ArrowRight aria-hidden="true" /></span>)}
          </div>
        </div>
      ))}
      <div className="xo-app-logout" data-tap="profile.logout"><Power aria-hidden="true" />{as('profile.logout')}</div>
    </Body>
  );
}

// ---------------------------------------------------------------- Your Health Monitor and Capacity

// How each metric looks at the top of its chart: icon, colour, the app's
// layout value, highest and lowest, unit, and the daily goal where it has one.
const METRIC_LOOK = {
  steps: [Footprints, STEPS, '6,412', '9,870', '3,105', '', '8,000'], calories: [Flame, CALORIES, '420', '610', '240', 'activity.kcal', '600'],
  distance: [Route, STEPS, '4.6', '7.1', '2.2', 'km', '6'], sleep: [Moon, SLEEP, '7h 32m', '8h 10m', '6h 04m', '', '8'],
  heartRate: [Heart, '#ff383c', '72', '118', '54', 'health.bpm'], heartRateAvg: [Heart, '#ff383c', '72', '118', '54', 'health.bpm'],
  restingHeartRate: [HeartPulse, '#ff383c', '58', '63', '54', 'health.bpm'], hrv: [Activity, '#30b0cd', '46', '61', '32', 'ms'],
  bloodOxygen: [Droplet, '#30b0cd', '98', '99', '95', '%'], bloodPressure: [Gauge, '#ff383c', '118/76', '126/82', '112/71', 'mmHg'],
  respiratoryRate: [Wind, '#30b0cd', '14', '17', '12', 'rpm'],
};
const MOVES = new Set(['steps', 'calories', 'distance']);

function MetricScreen({ as, metric, at }) {
  if (metric === 'capacity') return <CapacityScreen as={as} />;
  const [Icon, color, value, high, low, unit, goal] = METRIC_LOOK[metric] || [HeartPulse, APP_BLUE, '—', '—', '—', ''];
  const bars = [52, 64, 48, 70, 58, 76, 66];
  return (
    <>
      <PageBar title={as('homepage.health_monitor_title')} />
      <Body>
        <div className="xo-app-metric-picker" data-tap="metric.picker">
          <Icon aria-hidden="true" /><strong>{metricName(metric, as)}</strong><ChevronDown aria-hidden="true" />
        </div>
        <RangeTabs as={as} />
        <DateNav label={new Date(at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })} />
        <div className="xo-app-headline">
          <small>{as(MOVES.has(metric) ? 'common.today' : 'health.detail.latest')}</small>
          <strong>{value}<em>{unit && ` ${unitOf(unit, as)}`}</em>
            {goal && <><em className="is-goal"> / {goal}</em><span data-tap="metric.goal"><Pencil aria-hidden="true" /></span></>}
          </strong>
        </div>
        <div className="xo-app-metric-chart" style={{ '--xo-app-metric': color }} data-tap="metric.chart">
          {bars.map((height, index) => <i key={index} style={{ height: `${height}%` }} className={index === bars.length - 1 ? 'is-now' : ''} />)}
        </div>
        <div className="xo-app-facts is-two">
          <span className="xo-app-tile"><small>{as('common.highest')}</small><strong>{high}</strong></span>
          <span className="xo-app-tile"><small>{as('common.lowest')}</small><strong>{low}</strong></span>
        </div>
      </Body>
    </>
  );
}

function CapacityScreen({ as }) {
  const parts = [[Activity, 'health.capacity.hrv', '46 ms'], [HeartPulse, 'health.capacity.resting_hr', `58 ${as('health.bpm')}`], [Droplet, 'health.capacity.blood_oxygen', '98%']];
  return (
    <>
      <PageBar title={as('homepage.capacity')} />
      <Body className="is-centered">
        <svg className="xo-app-gauge" viewBox="0 0 200 120" aria-hidden="true">
          <path d="M20 110a80 80 0 0 1 160 0" stroke="#e5e5e6" />
          <path d="M20 110a80 80 0 0 1 137-56" stroke={CAPACITY} />
          <text x="100" y="100">7</text>
        </svg>
        <div className="xo-app-facts is-three">
          {parts.map(([Icon, label, shown]) => <span key={label} className="xo-app-tile"><Icon aria-hidden="true" /><small>{as(label)}</small><strong>{shown}</strong></span>)}
        </div>
        <h3 className="xo-app-heading">{as('health.capacity.today_vs_yesterday')}</h3>
        <div className="xo-app-compare">
          <span><small>{as('health.capacity.today')}</small><Bar share={0.7} tone="capacity" /></span>
          <span><small>{as('health.capacity.yesterday')}</small><Bar share={0.62} tone="muted" /></span>
        </div>
      </Body>
    </>
  );
}

// ---------------------------------------------------------------- habits

function HabitsScreen({ as, part, ticked }) {
  if (part === 'detail') {
    return (
      <>
        <PageBar title={as('habits.header')} action={<span data-tap="habits.edit"><Pencil aria-hidden="true" /></span>} />
        <Body className="is-centered">
          <Blank width={180} />
          <Blank width={130} />
          <span className={`xo-app-complete${ticked ? ' is-done' : ''}`} data-tap="habits.complete">
            <Check aria-hidden="true" /><b>{as(ticked ? 'habits.completed' : 'habits.complete')}</b>
          </span>
        </Body>
      </>
    );
  }
  if (part === 'add' || part === 'edit') {
    return (
      <>
        <PageBar title={as(part === 'add' ? 'habits.add_habits' : 'habits.edit_habits')} />
        <Body>
          <Field label={as('habits.habit_name')}><Blank width={140} /></Field>
          <Field label={as('habits.notes')}><Blank width={180} /></Field>
          {part === 'add'
            ? <span className="xo-app-gradient-button" data-tap="habits.submit">{as('habits.add_to_habits')}</span>
            : <>
              <span className="xo-app-gradient-button" data-tap="habits.save">{as('habits.save')}</span>
              <span className="xo-app-link is-danger" data-tap="habits.delete">{as('habits.delete_habit')}</span>
            </>}
        </Body>
      </>
    );
  }
  const done = ticked ? 3 : 2;
  return (
    <>
      <PageBar title={as('habits.header')} />
      <Body>
        <RangeTabs as={as} />
        <DateNav label={as('habits.today')} />
        <small className="xo-app-label">{as('habits.habits_label')}</small>
        <p className="xo-app-big">{done}<em className="is-cyan"> /3 {as('habits.completed')}</em></p>
        <Bar share={done / 3} tone="habit" />
        <span className="xo-app-note">{as('habits.goal_progress', { percent: Math.round((done / 3) * 100) })}</span>
        <div className="xo-app-list-head">
          <strong>{as('habits.add_habits')}</strong>
          <span className="xo-app-pill is-gradient" data-tap="habits.add">{as('habits.add_new_habit')}<CirclePlus aria-hidden="true" /></span>
        </div>
        {[0, 1, 2].map((index) => (
          <Glow key={index} tone="habit" className="xo-app-habit">
            <span className={`xo-app-check${index < done ? ' is-done' : ''}`} data-tap={index === 2 ? 'habits.check' : undefined}>{index < done && <Check aria-hidden="true" />}</span>
            <Blank width={150 - index * 22} light />
            <span data-tap={index === 0 ? 'habits.detail' : undefined}><ChevronRight aria-hidden="true" /></span>
          </Glow>
        ))}
        <h3 className="xo-app-heading">{as('habits.today_vs_yesterday')}</h3>
        <div className="xo-app-compare">
          <span><small>{as('habits.today')}</small><Bar share={done / 3} tone="habit" /></span>
          <span><small>{as('habits.yesterday')}</small><Bar share={1} tone="muted" /></span>
        </div>
      </Body>
    </>
  );
}

// ---------------------------------------------------------------- the diet tracker

const MEALS = ['breakfast', 'lunch', 'dinner', 'snacks'];
const mealOf = (hint) => (hint && MEALS.includes(hint.toLowerCase()) ? hint.toLowerCase() : 'breakfast');
const MACROS = [['nutrition.protein', 'protein', '82 g - 328'], ['nutrition.fat', 'fat', '51 g - 459'], ['nutrition.carbohydrates', 'carbs', '176 g - 704'], ['nutrition.fiber', 'fiber', '21 g']];

function Macros({ as }) {
  return (
    <div className="xo-app-macros">
      {MACROS.map(([label, tone, shown]) => (
        <span key={tone} className={`is-${tone}`}><b>{as(label)}</b><small>{shown}{tone !== 'fiber' && ` ${as('activity.kcal')}`}</small></span>
      ))}
    </div>
  );
}

function DietScreen({ as, part, hints }) {
  const meal = mealOf(hints.meal);
  if (part === 'add') {
    return (
      <>
        <PageBar title={as(`nutrition.add_${meal}`)} />
        <Body>
          <small className="xo-app-label">{as('nutrition.search_or_scan')}</small>
          <div className="xo-app-search-row">
            <div className="xo-app-search" data-tap="diet.search"><span>{as('nutrition.search_placeholder')}</span><Search aria-hidden="true" /></div>
            <span className="xo-app-camera" data-tap="diet.scan"><Camera aria-hidden="true" /></span>
          </div>
          <small className="xo-app-muted"><Info aria-hidden="true" />{as('nutrition.scan_hint')}</small>
          <div className="xo-app-tile xo-app-food"><Blank width={130} /><span data-tap="diet.food.clear"><Trash2 aria-hidden="true" /></span></div>
          <div className="xo-app-weight"><small>{as('nutrition.meal_weight')}</small><span><Minus aria-hidden="true" /><b>100 g</b><Plus aria-hidden="true" /></span></div>
          <h3 className="xo-app-heading is-small">{as('nutrition.meal_nutrients')}</h3>
          <Macros as={as} />
          <span className="xo-app-link" data-tap="diet.nutrients">{as('nutrition.view_full_list')}</span>
          <span className="xo-app-dark-button" data-tap="diet.submit">{as(`nutrition.add_to_${meal}`)}</span>
        </Body>
      </>
    );
  }
  if (part === 'meal') {
    return (
      <>
        <PageBar title={as(`nutrition.your_${meal}`)} />
        <Body>
          {[0, 1].map((index) => (
            <span key={index} className="xo-app-tile xo-app-food" data-tap={index === 0 ? 'diet.food' : undefined}>
              <Blank width={120 - index * 20} /><em>{index === 0 ? 320 : 180} {as('activity.kcal')}</em><ArrowRight aria-hidden="true" />
            </span>
          ))}
          <span className="xo-app-outline-button" data-tap="diet.more">{as('nutrition.add_meal')}<CirclePlus aria-hidden="true" /></span>
          <h3 className="xo-app-heading is-small">{as('nutrition.meal_nutrients')}</h3>
          <Macros as={as} />
          <span className="xo-app-link" data-tap="diet.nutrients">{as('nutrition.view_full_list')}</span>
        </Body>
      </>
    );
  }
  if (part === 'goal') {
    return (
      <>
        <PageBar title={as('nutrition.set_your_goal')} />
        <Body className="is-centered">
          <p className="xo-app-subtitle">{as('nutrition.set_daily_goal')}</p>
          <div className="xo-app-wheel"><span>1,800</span><strong>2,000 <em>{as('activity.kcal')}</em></strong><span>2,200</span></div>
          <small className="xo-app-muted">{as('nutrition.current_goal_value', { goal: '2,000' })}</small>
          <span className="xo-app-dark-button" data-tap="diet.goal.save">{as('common.save')}</span>
        </Body>
      </>
    );
  }
  return (
    <>
      <PageBar title={as('nutrition.diet_tracker_header')} />
      <Body>
        <RangeTabs as={as} />
        <DateNav label={as('nutrition.today')} />
        <small className="xo-app-label">{as('nutrition.calories_intake')}</small>
        <p className="xo-app-big">1,540<em className="is-orange"> /2,000 {as('activity.kcal')}</em><span data-tap="diet.goal"><Pencil aria-hidden="true" /></span></p>
        <Bar share={0.77} tone="diet" />
        <span className="xo-app-note">{as('nutrition.goal_progress', { percent: 77 })}</span>
        <Macros as={as} />
        <h3 className="xo-app-heading">{as('nutrition.log_meals')}</h3>
        {MEALS.map((name) => (
          <Glow key={name} tone="diet" tap={`diet.${name}`} className="xo-app-meal">
            <span><strong>{as(`nutrition.${name}`)}</strong><small><b>0</b> {as('activity.kcal')}</small></span>
            <CirclePlus aria-hidden="true" />
          </Glow>
        ))}
      </Body>
    </>
  );
}

// ---------------------------------------------------------------- activity

const ACTIVITY_TYPES = ['archery', 'badminton', 'barre', 'baseball', 'basketball', 'boxing', 'cycling', 'hiking', 'running', 'walking'];

function ActivityScreen({ as, part, running, hints }) {
  const picked = hints.activity || 'running';
  const name = as(`activity.type.${picked}`);
  if (part === 'picker') {
    const types = ACTIVITY_TYPES.includes(picked) ? ACTIVITY_TYPES : [picked, ...ACTIVITY_TYPES.slice(0, 9)];
    return (
      <>
        <PageBar title={as('activity.select_activity')} />
        <Body className="is-tight">
          <div className="xo-app-search" data-tap="activity.search"><Search aria-hidden="true" /><span>{as('activity.search_activity')}...</span></div>
          {types.map((type, index) => (
            <span key={type} className="xo-app-tile xo-app-activity-type" data-tap={`activity.pick.${type}${index === 0 ? ' activity.pick.any' : ''}`}>
              <Dumbbell aria-hidden="true" /><b>{as(`activity.type.${type}`)}</b><Star aria-hidden="true" />
            </span>
          ))}
        </Body>
      </>
    );
  }
  if (part === 'timer' || part === 'detail') {
    const timer = part === 'timer';
    const ongoing = timer && running;
    const time = (shown) => (timer && !running ? '--:--' : shown);
    return (
      <>
        <PageBar title={as('activity.title')} back={timer ? X : ChevronLeft} />
        <Body>
          {timer && <span className="xo-app-select"><Dumbbell aria-hidden="true" /><b>{name}</b><ChevronDown aria-hidden="true" /></span>}
          <div className="xo-app-info-card">
            <strong>{as('activity.workout_time')}</strong>
            <div>
              <span><small>{as('activity.start_time')}</small><b>{time('07:42')}</b></span>
              <span><small>{as('activity.end_time')}</small><b>{ongoing ? '--:--' : time('08:21')}</b></span>
              <span><small>{as('activity.duration')}</small><b>{ongoing ? '12:48' : time('39:06')}</b></span>
            </div>
          </div>
          {!timer && (
            <div className="xo-app-info-card">
              <strong>{as('activity.heart_rate')}</strong>
              <div>
                <span><small>{as('activity.hr_min')}</small><b>96 {as('activity.bpm')}</b></span>
                <span><small>{as('activity.hr_avg')}</small><b>128 {as('activity.bpm')}</b></span>
                <span><small>{as('activity.hr_max')}</small><b>162 {as('activity.bpm')}</b></span>
              </div>
              <svg className="xo-app-hr-line" viewBox="0 0 300 60" aria-hidden="true"><path d="M0 44 30 40 60 30 90 34 120 18 150 24 180 12 210 20 240 16 270 28 300 22" /></svg>
            </div>
          )}
          <div className="xo-app-info-card">
            <strong>{as('activity.calories_burned')}</strong>
            <div>
              <span><small>{as('activity.active_kcal')}</small><b>{timer && !running ? '--' : ongoing ? '96' : '312'} {as('activity.kcal')}</b></span>
              <span><small>{as('activity.total_kcal')}</small><b>{timer && !running ? '--' : ongoing ? '118' : '366'} {as('activity.kcal')}</b></span>
            </div>
          </div>
        </Body>
        {timer && <div className="xo-app-footer"><span className="xo-app-dark-button" data-tap="activity.start">{as(running ? 'activity.stop_activity' : 'activity.start_activity')}</span></div>}
      </>
    );
  }
  return (
    <>
      <PageBar title={as('activity.title')} action={<span data-tap="activity.add"><CirclePlus aria-hidden="true" /></span>} />
      <Body>
        <RangeTabs as={as} />
        <DateNav label={as('activity.today')} />
        {[picked, 'walking'].map((type, index) => (
          <span key={`${type}-${index}`} className="xo-app-tile xo-app-activity-card" data-tap={index === 0 ? 'activity.card' : undefined}>
            <Dumbbell aria-hidden="true" />
            <b>{as(`activity.type.${type}`)}</b>
            <span>
              <small>{as('activity.duration')}</small><em>{index === 0 && running ? as('activity.stop_activity') : index === 0 ? '39:06' : '24:10'}</em>
              <small>{as('activity.calories')}</small><em>{index === 0 ? 312 : 104} {as('activity.kcal')}</em>
            </span>
          </span>
        ))}
      </Body>
    </>
  );
}

// ---------------------------------------------------------------- games

const GAMES = ['slice_master', 'stack_tower', 'bubble_pop', 'flappy_bounce', 'color_switch', 'ball_sort', 'merge_2048', 'memory_match', 'shape_fit', 'tetris'];
const BLOCKS = ['#30b0cd', '#ff8d28', '#bb5ae0', '#34c759', '#ff383c', '#ffcc00'];

function GameCard({ as, game, tap }) {
  const [first, ...rest] = as(`games.${game}`).split(' ');
  return (
    <span className="xo-app-tile xo-app-game" data-tap={tap}>
      <span><strong><b>{first}</b>{rest.length > 0 && ` ${rest.join(' ')}`}</strong><small>{as(`games.${game}_desc`)}</small></span>
      <ChevronRight aria-hidden="true" />
    </span>
  );
}

function GamesList({ as, marked = true }) {
  const words = as('games.your_cognitive_data').split(' ');
  const strong = words.length > 2 ? 2 : words.length > 1 ? 1 : 0;
  return (
    <>
      <PageBar title={as('games.title')} back={ArrowLeft} />
      <Body>
        <h2 className="xo-app-games-head">{words.slice(0, words.length - strong).join(' ')} <b>{words.slice(words.length - strong).join(' ')}</b></h2>
        {GAMES.map((game) => <GameCard key={game} as={as} game={game} tap={marked ? `games.${game}` : undefined} />)}
      </Body>
    </>
  );
}

// A game's scores open as a sheet over the list of games.
function GameBoardSheet({ as, game }) {
  return (
    <>
      <div className="xo-app-sheet-head is-centered"><strong>{as(`games.${game}`)}</strong></div>
      <div className="xo-app-tabs is-even"><span className="is-active">{as('games.my_scores')}</span><span>{as('games.global_leaderboard')}</span></div>
      <div className="xo-app-facts is-two">
        <span className="xo-app-tile"><small>{as('games.best_score')}</small><strong>1,240</strong></span>
        <span className="xo-app-tile"><small>{as('games.global_rank')}</small><strong>#18</strong></span>
      </div>
      <span className="xo-app-gradient-button" data-tap="games.play">{as('games.play_now')}</span>
    </>
  );
}

function GamesScreen({ as, part }) {
  const [page, game = 'tetris'] = (part || 'list').split('.');
  if (page === 'board') return <GamesList as={as} marked={false} />;
  if (page === 'play') {
    return (
      <>
        <PageBar title={as(`games.${game}`)} back={X} action={<b className="xo-app-score">0</b>} />
        <Body className="is-centered">
          <div className="xo-app-playfield">
            {Array.from({ length: 48 }, (_, index) => <i key={index} style={index % 7 === 3 || index > 35 ? { background: BLOCKS[index % BLOCKS.length] } : undefined} />)}
          </div>
          <span className="xo-app-gradient-button" data-tap="games.again">{as('games.play_again')}</span>
        </Body>
      </>
    );
  }
  return <GamesList as={as} />;
}

// ---------------------------------------------------------------- consultations and clinic bookings

function Confirmed({ as }) {
  return (
    <Body className="is-centered is-confirmed">
      <span className="xo-app-success"><Check aria-hidden="true" /></span>
      <p className="xo-app-subtitle">{as('consultation.your_consultation')}</p>
      <h2 className="xo-app-page-title">{as('consultation.is_booked')}</h2>
      <span className="xo-app-dark-button">{as('consultation.done')}</span>
    </Body>
  );
}

function Calendar({ at }) {
  const today = new Date(at);
  const first = new Date(today.getFullYear(), today.getMonth(), 1);
  const lead = (first.getDay() + 6) % 7;
  const days = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
  const picked = Math.min(days, today.getDate() + 2);
  return (
    <div className="xo-app-calendar">
      <div className="xo-app-calendar-head">
        <span data-tap="booking.month.back"><ChevronLeft aria-hidden="true" /></span>
        <strong>{today.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}</strong>
        <span data-tap="booking.month.forward"><ChevronRight aria-hidden="true" /></span>
      </div>
      <div className="xo-app-calendar-days">
        {Array.from({ length: lead }, (_, index) => <i key={`lead-${index}`} />)}
        {Array.from({ length: days }, (_, index) => (
          <span key={index} className={index + 1 === picked ? 'is-picked' : index + 1 < today.getDate() ? 'is-off' : ''} data-tap={index + 1 === picked ? 'booking.date' : undefined}>{index + 1}</span>
        ))}
      </div>
    </div>
  );
}

const SLOTS = ['09:00', '09:30', '10:30', '13:00', '14:30', '16:00'];

function Slots() {
  return <div className="xo-app-slots">{SLOTS.map((slot, index) => <span key={slot} className={index === 2 ? 'is-picked' : ''} data-tap={index === 2 ? 'booking.slot' : undefined}>{slot}</span>)}</div>;
}

function BookingScreen({ as, part, booked, at, hints }) {
  if (part === 'flow' || part === 'flow_time') {
    if (booked) return <><PageBar title={as('consultation.book_consultation')} /><Confirmed as={as} /></>;
    if (part === 'flow_time') {
      return (
        <>
          <PageBar title={as('consultation.book_consultation')} />
          <Body>
            <h2 className="xo-app-page-title">{as('consultation.date_time_title')}</h2>
            <p className="xo-app-subtitle">{as('consultation.date_time_subtitle')}</p>
            <div className="xo-app-legend"><span className="is-limited">{as('consultation.limited')}</span><span className="is-off">{as('consultation.unavailable')}</span></div>
            <Calendar at={at} />
            <small className="xo-app-label">{as('consultation.available_time')}</small>
            <Slots />
            <span className="xo-app-dark-button" data-tap="booking.book">{as('consultation.book')}</span>
          </Body>
        </>
      );
    }
    const choice = (tap, label, picked) => (
      <span className={`xo-app-square${picked ? ' is-picked' : ''}`} data-tap={tap}><i>{picked && <Check aria-hidden="true" />}</i>{label}</span>
    );
    return (
      <>
        <PageBar title={as('consultation.book_consultation')} />
        <Body>
          <h2 className="xo-app-page-title">{as('consultation.questionnaire_title')}</h2>
          <p className="xo-app-subtitle">{as('consultation.questionnaire_subtitle')}</p>
          <strong className="xo-app-question">{as('consultation.do_you_have_test')}</strong>
          {choice('booking.type.PreTest booking.type.pre_test booking.option', as('consultation.pre_test_consultation'), hints.consult !== 'PostTest' && hints.consult !== 'post_test')}
          {choice('booking.type.PostTest booking.type.post_test', as('consultation.post_test_consultation'), hints.consult === 'PostTest' || hints.consult === 'post_test')}
          {choice('booking.clinician.Nutritionist booking.clinician.nutritionist', as('consultation.nutritionist'), false)}
          {choice('booking.clinician.GeneticCounsellor booking.clinician.genetic_counsellor', as('consultation.genetic_counsellor'), true)}
          <span className="xo-app-square is-goal" data-tap="booking.goal"><i />{as('consultation.goal_advice')}</span>
          <span className="xo-app-dark-button" data-tap="booking.next">{as('consultation.next')}</span>
        </Body>
      </>
    );
  }
  if (part === 'detail' || part === 'past') {
    const past = part === 'past';
    return (
      <>
        <PageBar title={as(past ? 'consultation.past_consultations' : 'consultation.details_title')} />
        <Body>
          {past && <h3 className="xo-app-heading is-small">{as('consultation.your_past_consultations')}</h3>}
          <div className="xo-app-tile xo-app-consult" data-tap={past ? 'booking.card' : undefined}>
            <strong>{as('consultation.genetic_counsellor')}</strong>
            <small>{as(past ? 'consultation.post_test_consult' : 'consultation.pre_test_consult')}</small>
            {[['consultation.date', dayLabel(at, past ? -12 : 2)], ['consultation.time', '10:30'], ['consultation.duration', `30 ${as('consultation.minutes')}`]].map(([label, shown]) => (
              <span key={label}><small>{as(label)}</small><b>{shown}</b></span>
            ))}
            <span><small>{as('consultation.consultant')}</small><Blank width={110} /></span>
          </div>
          {!past && <span className="xo-app-gradient-button" data-tap="booking.join"><Video aria-hidden="true" />{as('consultation.join')}</span>}
        </Body>
      </>
    );
  }
  if (part === 'rating') {
    return (
      <>
        <PageBar title={as('consultation.rate_experience')} />
        <Body className="is-centered">
          <p className="xo-app-subtitle">{as('consultation.how_was_consultation')}</p>
          <div className="xo-app-stars">{[1, 2, 3, 4, 5].map((star) => <span key={star} data-tap={`booking.star.${star}`}><Star aria-hidden="true" /></span>)}</div>
          <div className="xo-app-textarea">{as('consultation.leave_feedback')}</div>
          <div className="xo-app-two-buttons">
            <span className="xo-app-outline-button" data-tap="booking.skip">{as('consultation.skip')}</span>
            <span className="xo-app-gradient-button" data-tap="booking.submit">{as('consultation.submit_rating')}</span>
          </div>
        </Body>
      </>
    );
  }
  if (part === 'call') {
    return (
      <div className="xo-app-call">
        <span className="xo-app-call-avatar"><UserRound aria-hidden="true" /><small>{as('common.clinician')}</small></span>
        <p>{as('consultation.waiting_clinician')}</p>
        <span className="xo-app-call-self"><small>{as('common.you')}</small></span>
        <div className="xo-app-call-controls">
          <span><Mic aria-hidden="true" /></span>
          <span className="is-end" data-tap="booking.end"><PhoneOff aria-hidden="true" /></span>
          <span><Video aria-hidden="true" /></span>
        </div>
      </div>
    );
  }
  if (part === 'clinic' || part === 'clinic_flow') {
    if (booked) return <><PageBar title={as('consultation.book_consultation')} /><Confirmed as={as} /></>;
    if (part === 'clinic') {
      return (
        <>
          <PageBar title={as('homepage.consultations_title')} />
          <Body>
            <span className="xo-app-gradient-button" data-tap="booking.start">{as('consultation.book_consultation')}</span>
            <div className="xo-app-notice"><Clock aria-hidden="true" />{as('consultation.no_upcoming')}</div>
          </Body>
        </>
      );
    }
    return (
      <>
        <PageBar title={as('consultation.book_consultation')} />
        <div className="xo-app-steps"><i className="is-on" /><i className="is-on" /><i /><i /></div>
        <Body>
          <small className="xo-app-label">{as('consultation.select_test_type')}</small>
          {['consultation.genetic_counsellor', 'consultation.nutritionist', 'consultation.consultant'].map((type, index) => (
            <span key={type} className={`xo-app-option${index === 0 ? ' is-picked' : ''}`} data-tap={index === 0 ? 'booking.option' : undefined}>
              <i className="xo-app-option-icon"><CalendarDays aria-hidden="true" /></i><strong>{as(type)}</strong>
            </span>
          ))}
          <small className="xo-app-label">{as('consultation.date_time_title')}</small>
          <div className="xo-app-date-strip">
            {[0, 1, 2, 3, 4].map((offset) => (
              <span key={offset} className={offset === 1 ? 'is-picked' : ''} data-tap={offset === 1 ? 'booking.date' : undefined}>
                <small>{new Date(at + offset * 86400000).toLocaleDateString(undefined, { weekday: 'short' })}</small>
                <b>{new Date(at + offset * 86400000).getDate()}</b>
              </span>
            ))}
          </div>
          <Slots />
          <span className="xo-app-dark-button" data-tap="booking.confirm">{as('common.continue')}</span>
        </Body>
      </>
    );
  }
  return (
    <>
      <PageBar title={as('consultation.header')} action={<span data-tap="booking.past"><History aria-hidden="true" /></span>} />
      <Body>
        {hints.consulted ? (
          <>
            <h3 className="xo-app-heading is-small">{as('consultation.your_consultations')}</h3>
            <div className="xo-app-tile xo-app-consult" data-tap="booking.card">
              <strong>{as('consultation.genetic_counsellor')}</strong>
              <small>{as('consultation.pre_test_consult')}</small>
              <span><small>{as('consultation.date')}</small><b>{dayLabel(at, 2)}</b></span>
            </div>
          </>
        ) : (
          <div className="xo-app-empty">
            <p>{as('consultation.no_upcoming')}</p>
            <strong>{as('consultation.book_first')}</strong>
          </div>
        )}
        <span className="xo-app-dark-button" data-tap="booking.book_now">{as('consultation.book_now')}</span>
      </Body>
    </>
  );
}

// ---------------------------------------------------------------- devices

const SEARCHING = { ring: 'device.searching_for_ring', strap: 'device.searching_for_strap', watch: 'device.searching_for_watch' };
const ADAPTERS = { ring: 'yc_ring', band: 'qc_band' };

function DeviceArtFor({ kind, size }) {
  return kind === 'band' ? <BandArt size={size} /> : <RingArt size={size} />;
}

function DeviceScreen({ as, part, hints }) {
  const opened = hints.device === 'qc_band' ? 'band' : hints.device === 'yc_ring' ? 'ring' : null;
  const kind = hints.pick === 'strap' ? 'band' : hints.pick === 'ring' ? 'ring' : opened || hints.kinds[0];
  if (part === 'detail') {
    const shown = opened || hints.kinds[0];
    return (
      <>
        <PageBar title={as('device.device_settings')} />
        <Body>
          <div className="xo-app-device-head">
            {shown === 'band' ? <BandIcon /> : <RingIcon />}
            <strong>{DEVICE_NAMES[shown]}</strong>
            <small><Cloud aria-hidden="true" />{as('device.last_synced')}  {as('device.just_now')}</small>
          </div>
          <div className="xo-app-tile xo-app-battery">
            <strong>{as('device.battery')}</strong>
            <b>84%</b>
            <Bar share={0.84} tone="battery" />
          </div>
          <span className="xo-app-tile xo-app-setting is-plain" data-tap="device.priority"><b>{as('device.make_device_priority')}</b><i className="xo-app-switch is-on" /></span>
          <span className="xo-app-tile xo-app-setting" data-tap="device.sync"><Cloud aria-hidden="true" /><b>{as('device.sync_now')}</b><RefreshCw aria-hidden="true" /></span>
          <small className="xo-app-label is-plain">{as('device.device_detail')}</small>
          <div className="xo-app-tile xo-app-detail-rows">
            <span><small>{as('device.model_number')}</small><Blank width={60} /></span>
            <span><small>{as('device.bluetooth')}</small><Blank width={110} /></span>
            <span><small>{as('device.monitoring_interval')}</small><b>10 min</b></span>
          </div>
          <span className="xo-app-tile xo-app-setting is-danger" data-tap="device.unpair"><Unlink aria-hidden="true" /><b>{as('device.unpair_device')}</b><ChevronRight aria-hidden="true" /></span>
        </Body>
      </>
    );
  }
  if (part === 'add') {
    return (
      <>
        <PageBar title={as('device.add_device')} />
        <Body>
          {[['ring', RingIcon, 'device.ring'], ['strap', BandIcon, 'device.band'], ['watch', Watch, 'device.watch']].map(([key, Icon, label]) => (
            <span key={key} className={`xo-app-tile xo-app-setting${key === 'watch' ? ' is-soon' : ''}`} data-tap={`device.pick.${key}`}>
              <Icon aria-hidden="true" /><b>{as(label)}</b>{key === 'watch' && <small>{as('device.coming_soon')}</small>}<ChevronRight aria-hidden="true" />
            </span>
          ))}
        </Body>
      </>
    );
  }
  if (part === 'scan') {
    return (
      <>
        <PageBar title={as('device.add_device')} />
        <Body className="is-centered">
          <span className="xo-app-pulse" data-tap="device.scan"><i /><i /><i /><Bluetooth aria-hidden="true" /></span>
          <h2 className="xo-app-page-title">{as(SEARCHING[hints.pick] || 'device.scanning_for_devices')}</h2>
          <p className="xo-app-subtitle">{as('device.scanning_hint')}</p>
        </Body>
      </>
    );
  }
  if (part === 'found') {
    return (
      <>
        <PageBar title={as('device.add_device')} />
        <Body className="is-centered">
          <Bluetooth className="xo-app-found-icon" aria-hidden="true" />
          <small className="xo-app-muted">{as('device.device_found')}</small>
          <h2 className="xo-app-page-title">{DEVICE_NAMES[kind]}</h2>
          <span className="xo-app-found-art"><DeviceArtFor kind={kind} size={230} /></span>
          <span className="xo-app-gradient-button is-full" data-tap="device.pair">{as('device.pair')}</span>
          <span className="xo-app-outline-button is-full" data-tap="device.not_mine">{as('device.this_is_not_my_device')}</span>
        </Body>
      </>
    );
  }
  if (part === 'paired') {
    return (
      <>
        <PageBar title={as('device.your_devices')} />
        <Body className="is-centered">
          <span className="xo-app-success"><Check aria-hidden="true" /></span>
          <h2 className="xo-app-page-title">{as('device.your_device_is_paired')}</h2>
          <p className="xo-app-subtitle">{as('device.device_paired_success')}</p>
          <div className="xo-app-paired-card">
            <span className="xo-app-paired-art"><DeviceArtFor kind={kind} size={40} /></span>
            <span><strong>{DEVICE_NAMES[kind]}</strong><small><i />{as('device.connected')}</small></span>
            <CircleCheck aria-hidden="true" />
          </div>
          <span className="xo-app-dark-button is-full">{as('common.done')}</span>
        </Body>
      </>
    );
  }
  return (
    <>
      <PageBar title={as('device.your_devices')} />
      <Body>
        {hints.kinds.map((shown, index) => (
          <span key={shown} className="xo-app-tile xo-app-device-card" data-tap={`device.card.${ADAPTERS[shown]}${index === 0 ? ' device.card' : ''}`}>
            {shown === 'band' ? <BandIcon /> : <RingIcon />}
            <span><strong>{DEVICE_NAMES[shown]}</strong><small>{as('device.just_now')}</small></span>
            <b className="is-good">{index === 0 ? 84 : 61}%</b>
            {index === 0 && <Star className="xo-app-star" aria-hidden="true" />}
          </span>
        ))}
        <span className="xo-app-add-device" data-tap="device.add"><span>{highlighted(as('homepage.add_tech_device'))}</span><CirclePlus aria-hidden="true" /></span>
      </Body>
    </>
  );
}

// ---------------------------------------------------------------- signing in

function SignInScreen({ as }) {
  return (
    <div className="xo-app-welcome">
      <AppLogo height={96} />
      <h1>{as('init.title')}</h1>
      <p>{as('init.subtitle')}</p>
      <div className="xo-app-primary" data-tap="signin.join">{as('init.button.join')}</div>
      <div className="xo-app-secondary" data-tap="signin.signin">{as('signin.button')}</div>
    </div>
  );
}

function LaunchScreen() {
  return <div className="xo-app-launch"><AppLogo height={72} /></div>;
}

// ---------------------------------------------------------------- one screen of the app

// The tab a page belongs to when it shows the tab bar; pages pushed over a tab
// carry their own bar instead.
function tabOf(screen, part) {
  if (screen === 'home' || screen === 'insights') return screen;
  if (screen === 'store' && part === 'store') return 'store';
  if (screen === 'profile' && !part) return 'profile';
  if (screen === 'ai' && part !== 'voice') return 'ai';
  return undefined;
}
const DARK_PAGES = new Set(['ai:voice', 'booking:call']);
// A sheet opens over the page under it, which stays put rather than sliding in again.
const PAGE_UNDER_SHEET = { 'ai:history': 'ai:chat', 'checkout:null': 'store:cart' };
// Pages slide in from the right as they open and from the left as the person goes back.
const SLIDE = {
  arriving: (back) => ({ opacity: 0, x: back ? -18 : 18 }),
  shown: { opacity: 1, x: 0 },
  leaving: (back) => ({ opacity: 0, x: back ? 18 : -18 }),
};

// One screen of the app. `part` is the page, metric or omics section it shows;
// `moment` carries what a step did on it: a chat message sent, a habit
// ticked, something added to the cart. `hints` is what the rest of the
// session says about it (see screenHints).
export function AppScreen({ screen, part = null, moment = null, person, at, as, hints = { kinds: ['ring'], cart: 0 }, back = false }) {
  let body;
  switch (screen) {
    case 'launch': body = <LaunchScreen />; break;
    case 'signin': body = <SignInScreen as={as} />; break;
    case 'insights': body = <InsightsScreen as={as} />; break;
    case 'results': body = <ResultsScreen as={as} section={part} />; break;
    case 'omics': body = <OmicsScreen as={as} section={part} />; break;
    case 'clinical': body = <ClinicalScreen as={as} />; break;
    case 'reports': body = <ReportsScreen as={as} part={part} />; break;
    case 'ai': body = <AiScreen as={as} part={part} chatting={moment === 'chat'} />; break;
    case 'store': body = <StoreScreen as={as} part={part} added={moment === 'added'} hints={hints} />; break;
    case 'checkout': body = <CartPage as={as} />; break;
    case 'orders': body = <OrdersScreen as={as} />; break;
    case 'tests': body = <TestsScreen as={as} />; break;
    case 'profile': body = <ProfileScreen as={as} person={person} part={part} hints={hints} />; break;
    case 'metric': body = <MetricScreen as={as} metric={part} at={at} />; break;
    case 'habits': body = <HabitsScreen as={as} part={part} ticked={moment === 'ticked'} />; break;
    case 'diet': body = <DietScreen as={as} part={part} hints={hints} />; break;
    case 'activity': body = <ActivityScreen as={as} part={part} running={moment === 'running'} hints={hints} />; break;
    case 'games': body = <GamesScreen as={as} part={part} />; break;
    case 'booking': body = <BookingScreen as={as} part={part} booked={moment === 'booked'} at={at} hints={hints} />; break;
    case 'device': body = <DeviceScreen as={as} part={part} hints={hints} />; break;
    default: body = <HomeScreen as={as} at={at} hints={hints} />;
  }
  // Sheets slide up over the page under them, tab bar included.
  let sheet = null;
  if (screen === 'ai' && part === 'history') sheet = <AiHistorySheet as={as} />;
  if (screen === 'games' && part && part.startsWith('board.')) sheet = <GameBoardSheet as={as} game={part.split('.')[1]} />;
  if (screen === 'checkout') sheet = <AddressSheet as={as} paid={moment === 'paid'} />;
  const tab = tabOf(screen, part);
  const bare = screen === 'launch' || screen === 'signin';
  const dark = DARK_PAGES.has(`${screen}:${part}`);
  // The page carries its own bars, so they come and go with it.
  const kind = bare ? ' is-bare' : tab === undefined ? ' is-pushed' : ' is-tab';
  return (
    <div className="xo-app-screen">
      <AnimatePresence mode="wait" initial={false} custom={back}>
        <motion.div key={PAGE_UNDER_SHEET[`${screen}:${part}`] || (part && part.startsWith('board.') ? 'games:list' : `${screen}:${part}`)}
          className={`xo-app-page is-${screen}${kind}${dark ? ' is-dark-page' : ''}`} custom={back} variants={SLIDE}
          initial="arriving" animate="shown" exit="leaving" transition={{ duration: 0.16, ease: 'easeOut' }}>
          {tab !== undefined && <AppBar />}
          {body}
          {tab !== undefined && <BottomNav active={tab} as={as} />}
        </motion.div>
      </AnimatePresence>
      <AnimatePresence>
        {sheet && (
          <motion.div key={`${screen}:${part}`} className="xo-app-modal" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}>
            <div className="xo-app-scrim" />
            <motion.div className={`xo-app-bottom-sheet${screen === 'checkout' ? ' is-tall' : ''}`} initial={{ y: 80 }} animate={{ y: 0 }}
              transition={{ duration: 0.28, ease: 'easeOut' }}>
              <i className="xo-app-grabber" />
              {sheet}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
